import React, { forwardRef } from 'react'
import { Grid as BaseGrid, Divider as DividerBase, styled } from '@mui/material'
import {
  ArrowDownward as BaseArrowDownward,
  Close as BaseCloseIcon,
  ExpandMore as ExpandMoreBase,
  FileDownload as BaseFileDownloadIcon,
  Help as HelpIcon
} from '@mui/icons-material'
import {
  Radio as BaseRadio,
  Tab as BaseTab,
  Tabs as BaseTabs,
  Tooltip as TooltipBase,
  WarningIcon as BaseWarningIcon
} from '@chilecompra/react-kit/components'

export const Anchor = styled('div')`
  border-bottom: 1px solid ${props => props.theme.palette.gray[85]};
  cursor: pointer;
  display: inline-flex;
  min-width: 60px;
`
export const AnchorText = styled(({ active, ...props }) => <span {...props} />)`
  color: ${props => (props.active ? props.theme.palette.brand.primary : props.theme.palette.brand.black2)};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 16px;
  letter-spacing: 0;
  line-height: 20px;
  pointer-events: none;
  margin: 0 16px 0 0;
  user-select: none;
`
export const AnchorIcon = styled(({ open, ...props }) => <ExpandMoreBase {...props} />)`
  &.MuiSvgIcon-root {
    fill: ${props => props.theme.palette.gray[50]};
    font-size: 24px;
    margin: 0 0 0 auto;
    pointer-events: none;
    transform: ${props => (props.open ? 'rotate(180deg)' : 'none')};
    user-select: none;
  }
`
export const CloseIcon = styled(BaseCloseIcon)`
  padding: 10px;
  position: absolute;
  right: 0;
  top: 0;
`
export const Divider = styled(DividerBase)`
  width: 100%;
`
export const DownloadBreach = styled('a')`
  color: ${props => props.theme.palette.brand.black3};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  text-decoration: underline;
  width: 100%;
`
export const Grid = styled(BaseGrid)`
  margin: ${props => props.margin};
  padding: ${props => props.padding};
  width: 100%;
`
export const IconArrowDownward = styled(BaseArrowDownward)`
  color: ${props => props.theme.palette.brand.black3};
  margin: ${props => props.margin};
`
export const Link = styled('a')`
  color: ${props => props.theme.palette.gray[50]};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  margin: ${props => props.margin};
  text-decoration: underline;
  width: 100%;
`
export const Action = styled(({ display, color, justifyContent, ...props }) => <span {...props} />)`
  color: ${({ color = 'gray', theme }) => theme.palette[color][50]};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  text-decoration: underline;
  width: 100%;
  display: ${({ display = 'inline' }) => display};
  justify-content: ${({ justifyContent = 'flex-start' }) => justifyContent};
  visibility: ${props => props.visibility};
`
export const LineJump = styled('br')``

export const NotFoundImg = styled('img')`
  color: ${props => props.theme.palette.brand.black3};
  height: auto;
  width: 82px;
`
export const WarningIcon = styled(BaseWarningIcon)`
  color: ${props => props.theme.palette.yellow[50]};
`
export const WrapperDownloadBreach = styled('div')`
  align-items: center;
  display: flex;
  margin: 16px 0 0;
`
export const WrapperLabelCheck = styled('div')`
  margin: 0 0 0 -16px;
`

export const Paragraph = styled('p')`
  margin: 0;
`

export const TooltipIcon = styled(forwardRef(({ size, ...props }, ref) => <HelpIcon {...props} ref={ref} />))`
  &.MuiSvgIcon-root {
    fill: ${props => props.theme.palette.gray[50]};
    font-size: ${props => {
      if (props.size === 'large') {
        return '1.1rem'
      }

      return '0.8rem'
    }};
    margin: 0 0 0 4px;
  }
`

export const LinkParticipant = styled('a')`
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 400;
  line-height: 16.41px;
  text-align: ${props => props.textAlign || 'left'};
  text-decoration: ${props => props.textDecoration || 'none'};
`

export const TooltipText = styled('span')`
  margin: 0;
`

export const BaseContainer = styled(React.forwardRef((props, ref) => <BaseGrid container ref={ref} {...props} />))`
  &.MuiGrid-container {
    margin: ${props => props.margin};
    padding: ${props => props.padding};
  }
`

export const BaseItem = styled(React.forwardRef((props, ref) => <BaseGrid item ref={ref} {...props} />))`
  &.MuiGrid-item {
    margin: ${props => props.margin};
    padding: ${props => props.padding};
  }
`

export const Container = styled(BaseContainer)``

export const DialogButtons = styled(BaseContainer)``

export const DialogContent = styled(BaseContainer)`
  &.MuiGrid-container {
    ${props => props.theme.breakpoints.only('sm')} {
      padding: 32px 16px;
    }

    ${props => props.theme.breakpoints.only('xs')} {
      padding: 32px 0;
    }
  }
`

export const DialogMessage = styled(BaseContainer)`
  &.MuiGrid-container {
    max-height: 575px;
    overflow: auto;
    padding-inline-end: 8px;
  }
`

export const DownloadIcon = styled(BaseFileDownloadIcon)`
  &.MuiSvgIcon-root {
    color: ${props => props.theme.palette.blue[50]};
    height: 16px;
    width: 16px;
  }
`

export const DownloadLink = styled('a')`
  align-items: center;
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  display: inline-flex;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
  text-decoration: underline;

  &:hover {
    color: ${props => props.theme.palette.blue[30]};
  }
`

export const Item = styled(BaseItem)``

export const MoreInformationLink = styled('div')`
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 500;
  margin-top: 6px;
  text-decoration: underline;

  &:hover {
    color: ${props => props.theme.palette.blue[30]};
  }
`

export const Radio = styled(BaseRadio)`
  &.MuiFormGroup-root {
    &[direction='horizontal'] {
      display: flex;
      flex-direction: row;
      gap: 32px;
      margin: 0;
    }

    &[direction='vertical'] {
      margin: 0;

      .MuiFormControlLabel-root {
        margin: 0 0 16px;
      }
    }
  }
`

export const Strong = styled('span')`
  font-weight: bold;
`

export const Tab = styled(BaseTab)`
  height: 220px;
  min-height: 220px;
  overflow: auto;
`

export const Tabs = styled(BaseTabs)`
  &.MuiTabs-root .MuiTab-root {
    padding: 0;
  }
`

export const Tooltip = styled(TooltipBase)`
  &.MuiPopper-root .MuiTooltip-tooltip {
    max-height: 100%;

    > div {
      max-height: 100%;
      word-break: auto-phrase;
    }
  }
`
